import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@cargomatic/ui';
import { useMutation } from '@tanstack/react-query';
import { useShipmentById } from './useShipmentById';
import ShipmentStore from 'stores/ShipmentStore';
import SessionStore from 'stores/SessionStore';
import { addOrderAdjustments } from 'utils/OrderWebAPIUtils';
import { addAdjustment } from 'actions/ShipmentsActionCreators';
export function useOrderRtbActions({ orderId, arLines, apLines, refetchArLines, refetchApLines, currency = 'USD', }) {
    var _a;
    const { toast } = useToast();
    // Single active action state
    const [activeAction, setActiveAction] = useState(null);
    const emptyChargeSection = {
        accessorialName: '',
        amount: '',
        adjustmentReason: '',
    };
    // Base cost dialog state
    const [dialogEquipmentId, setDialogEquipmentId] = useState('');
    const [dialogBaseCost, setDialogBaseCost] = useState(emptyChargeSection);
    const [dialogFsc, setDialogFsc] = useState(emptyChargeSection);
    const [dialogBaseCostInvoiced, setDialogBaseCostInvoiced] = useState(false);
    const [dialogFscInvoiced, setDialogFscInvoiced] = useState(false);
    const [dialogOriginalBaseCostAmount, setDialogOriginalBaseCostAmount] = useState('');
    const [dialogOriginalFscAmount, setDialogOriginalFscAmount] = useState('');
    const [dialogOriginalEquipmentId, setDialogOriginalEquipmentId] = useState('');
    // Pending refetch tracking
    const [pendingRefetch, setPendingRefetch] = useState(null);
    // Single shipment fetch based on active action
    const shipmentId = activeAction && 'charge' in activeAction
        ? (_a = activeAction.charge.shipment) === null || _a === void 0 ? void 0 : _a._id
        : undefined;
    const { data: shipment, isLoading: isLoadingShipment } = useShipmentById(shipmentId, {
        enabled: Boolean(activeAction),
        staleTime: 5000, // 5 seconds - shorter stale time
    });
    // Listen to ShipmentStore for successful updates and trigger refetch
    useEffect(() => {
        if (!pendingRefetch)
            return;
        const handleStoreChange = () => {
            if (pendingRefetch === 'receivables') {
                refetchArLines();
            }
            else {
                refetchApLines();
            }
            setPendingRefetch(null);
        };
        ShipmentStore.addChangeListener(handleStoreChange);
        return () => {
            ShipmentStore.removeChangeListener(handleStoreChange);
        };
    }, [pendingRefetch, refetchArLines, refetchApLines]);
    // Helper to convert tab type to ledger type
    const toLedgerType = (tab) => tab === 'ar' ? 'receivables' : 'payables';
    // Clear active action
    const clearAction = useCallback(() => {
        setActiveAction(null);
        setDialogEquipmentId('');
        setDialogBaseCost({
            accessorialName: '',
            amount: '',
            adjustmentReason: '',
        });
        setDialogFsc({ accessorialName: '', amount: '', adjustmentReason: '' });
        setDialogBaseCostInvoiced(false);
        setDialogFscInvoiced(false);
        setDialogOriginalBaseCostAmount('');
        setDialogOriginalFscAmount('');
        setDialogOriginalEquipmentId('');
    }, []);
    // Action triggers
    const editCharge = useCallback((charge, tab) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        const ledger = toLedgerType(tab);
        const isOrderLevel = !((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a.shipment_id);
        const isBaseCostOrFsc = charge.category === 'base_cost' || charge.type === 'fuel_surcharge';
        if (isOrderLevel && isBaseCostOrFsc) {
            // Pre-populate the dialog with existing values
            setDialogEquipmentId((_c = (_b = charge.equipment) === null || _b === void 0 ? void 0 : _b._id) !== null && _c !== void 0 ? _c : '');
            const lines = tab === 'ar' ? arLines : apLines;
            const equipmentId = (_d = charge.equipment) === null || _d === void 0 ? void 0 : _d._id;
            // Only look at order-level lines (no shipment) for the same equipment
            const orderLevelLines = lines.filter((c) => { var _a; return !((_a = c.shipment) === null || _a === void 0 ? void 0 : _a.shipment_id); });
            const existingBaseCost = orderLevelLines.find((c) => {
                var _a;
                return ((_a = c.equipment) === null || _a === void 0 ? void 0 : _a._id) === equipmentId &&
                    c.category === 'base_cost' &&
                    c.type !== 'fuel_surcharge';
            });
            const existingFsc = orderLevelLines.find((c) => { var _a; return ((_a = c.equipment) === null || _a === void 0 ? void 0 : _a._id) === equipmentId && c.type === 'fuel_surcharge'; });
            const baseCostAmount = (existingBaseCost === null || existingBaseCost === void 0 ? void 0 : existingBaseCost.value)
                ? String(existingBaseCost.value)
                : '';
            const fscAmount = (existingFsc === null || existingFsc === void 0 ? void 0 : existingFsc.value) ? String(existingFsc.value) : '';
            setDialogBaseCost({
                accessorialName: (_e = existingBaseCost === null || existingBaseCost === void 0 ? void 0 : existingBaseCost.message) !== null && _e !== void 0 ? _e : '',
                amount: baseCostAmount,
                adjustmentReason: (_f = existingBaseCost === null || existingBaseCost === void 0 ? void 0 : existingBaseCost.notes) !== null && _f !== void 0 ? _f : '',
            });
            setDialogFsc({
                accessorialName: (_g = existingFsc === null || existingFsc === void 0 ? void 0 : existingFsc.message) !== null && _g !== void 0 ? _g : '',
                amount: fscAmount,
                adjustmentReason: (_h = existingFsc === null || existingFsc === void 0 ? void 0 : existingFsc.notes) !== null && _h !== void 0 ? _h : '',
            });
            setDialogBaseCostInvoiced(Boolean((_j = existingBaseCost === null || existingBaseCost === void 0 ? void 0 : existingBaseCost.invoice_id) === null || _j === void 0 ? void 0 : _j.number));
            setDialogFscInvoiced(Boolean((_k = existingFsc === null || existingFsc === void 0 ? void 0 : existingFsc.invoice_id) === null || _k === void 0 ? void 0 : _k.number));
            setDialogOriginalBaseCostAmount(baseCostAmount);
            setDialogOriginalFscAmount(fscAmount);
            setDialogOriginalEquipmentId((_m = (_l = charge.equipment) === null || _l === void 0 ? void 0 : _l._id) !== null && _m !== void 0 ? _m : '');
            setActiveAction({ type: 'edit-base-cost', charge, ledger });
            return;
        }
        if (!((_o = charge.shipment) === null || _o === void 0 ? void 0 : _o._id)) {
            toast({
                title: 'Error',
                description: 'No shipment associated with this charge.',
                variant: 'destructive',
            });
            return;
        }
        setActiveAction({ type: 'edit', charge, ledger });
    }, [toast, arLines, apLines]);
    const addInternalAdjustment = useCallback((charge, tab) => {
        var _a;
        if (!((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id)) {
            toast({
                title: 'Error',
                description: 'No shipment associated with this charge.',
                variant: 'destructive',
            });
            return;
        }
        setActiveAction({
            type: 'internal-adjustment',
            charge,
            ledger: toLedgerType(tab),
        });
    }, [toast]);
    const addCustomerAdjustment = useCallback((charge, tab) => {
        var _a;
        if (!((_a = charge.shipment) === null || _a === void 0 ? void 0 : _a._id)) {
            toast({
                title: 'Error',
                description: 'No shipment associated with this charge.',
                variant: 'destructive',
            });
            return;
        }
        setActiveAction({
            type: 'customer-adjustment',
            charge,
            ledger: toLedgerType(tab),
        });
    }, [toast]);
    const addBaseCost = useCallback((tab) => {
        setDialogEquipmentId('');
        setDialogBaseCost({
            accessorialName: '',
            amount: '',
            adjustmentReason: '',
        });
        setDialogFsc({ accessorialName: '', amount: '', adjustmentReason: '' });
        setDialogBaseCostInvoiced(false);
        setDialogFscInvoiced(false);
        setDialogOriginalBaseCostAmount('');
        setDialogOriginalFscAmount('');
        setActiveAction({ type: 'add-base-cost', ledger: toLedgerType(tab) });
    }, []);
    // Save handlers
    const saveAdjustment = useCallback((adjustment) => {
        if (!shipment || !activeAction)
            return;
        addAdjustment(shipment, activeAction.ledger, adjustment);
        setPendingRefetch(activeAction.ledger);
        clearAction();
    }, [shipment, activeAction, clearAction]);
    const adjustmentMutation = useMutation({
        mutationFn: (params) => addOrderAdjustments(orderId, params.adjustments),
        onSuccess: (_data, variables) => {
            toast({
                title: 'Adjustment Saved',
                description: 'The adjustment has been saved successfully.',
            });
            if (variables.ledger === 'receivables') {
                refetchArLines();
            }
            else {
                refetchApLines();
            }
            clearAction();
        },
        onError: () => {
            toast({
                title: 'Error',
                description: 'Failed to save adjustment. Please try again.',
                variant: 'destructive',
            });
        },
    });
    const saveBaseCost = useCallback(() => {
        if (!activeAction ||
            (activeAction.type !== 'add-base-cost' &&
                activeAction.type !== 'edit-base-cost'))
            return;
        if (!orderId)
            return;
        if (!dialogEquipmentId) {
            toast({
                title: 'Equipment Required',
                description: 'Please select an equipment before saving.',
                variant: 'destructive',
            });
            return;
        }
        const baseCostAmount = parseFloat(dialogBaseCost.amount);
        const fscAmount = parseFloat(dialogFsc.amount);
        const hasBaseCost = !isNaN(baseCostAmount) && baseCostAmount > 0;
        const hasFsc = !isNaN(fscAmount) && fscAmount > 0;
        const costType = activeAction.ledger === 'receivables' ? 'shipper_cost' : 'carrier_cost';
        const adjustments = [];
        const origBaseCostNum = dialogOriginalBaseCostAmount
            ? Number(dialogOriginalBaseCostAmount)
            : NaN;
        const origFscNum = dialogOriginalFscAmount
            ? Number(dialogOriginalFscAmount)
            : NaN;
        const baseCostChanged = isNaN(origBaseCostNum) ||
            baseCostAmount !== origBaseCostNum ||
            dialogBaseCost.adjustmentReason !== '';
        const fscChanged = isNaN(origFscNum) ||
            fscAmount !== origFscNum ||
            dialogFsc.adjustmentReason !== '';
        const shouldIncludeBase = baseCostChanged && !isNaN(baseCostAmount);
        const shouldIncludeFsc = fscChanged && !isNaN(fscAmount);
        if (!shouldIncludeBase && !shouldIncludeFsc && !hasBaseCost && !hasFsc) {
            toast({
                title: 'Invalid Value',
                description: 'Please enter at least one valid amount.',
                variant: 'destructive',
            });
            return;
        }
        if (shouldIncludeBase) {
            adjustments.push({
                type: costType,
                category: 'base_cost',
                adjustedItem: 'base_cost',
                message: dialogBaseCost.accessorialName ||
                    (activeAction.ledger === 'receivables'
                        ? 'Shipper Cost'
                        : 'Carrier Cost'),
                value: baseCostAmount,
                reason: dialogBaseCost.adjustmentReason || undefined,
                equipment: dialogEquipmentId,
            });
        }
        if (shouldIncludeFsc) {
            adjustments.push({
                type: 'fuel_surcharge',
                category: 'accessorials',
                adjustedItem: 'fuel_surcharge',
                message: dialogFsc.accessorialName || 'Fuel Surcharge',
                value: fscAmount,
                reason: dialogFsc.adjustmentReason || undefined,
                equipment: dialogEquipmentId,
            });
        }
        if (adjustments.length === 0) {
            clearAction();
            return;
        }
        adjustmentMutation.mutate({
            ledger: activeAction.ledger,
            adjustments,
        });
    }, [
        activeAction,
        orderId,
        dialogEquipmentId,
        dialogBaseCost,
        dialogFsc,
        dialogOriginalBaseCostAmount,
        dialogOriginalFscAmount,
        toast,
        adjustmentMutation,
    ]);
    // Utility: Check if user can add adjustments
    const canAddAdjustment = useCallback((charge) => {
        const isFinanceUser = SessionStore.hasFinanceRole();
        const shipmentIsCurrent = true; // Simplified for order-level view
        return ((isFinanceUser || shipmentIsCurrent) &&
            charge.category !== 'rate_adjustments' &&
            charge.category !== 'incentive_payments');
    }, []);
    // Utility: Check if order needs base cost
    const orderNeedsBaseCost = useCallback((tab) => {
        const allCharges = tab === 'ar' ? arLines : apLines;
        return !allCharges.some((c) => { var _a; return c.category === 'base_cost' && !((_a = c.shipment) === null || _a === void 0 ? void 0 : _a.shipment_id); });
    }, [arLines, apLines]);
    // Derived state for modals
    const isEditModalOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'edit';
    const isInternalAdjustmentModalOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'internal-adjustment';
    const isCustomerAdjustmentModalOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'customer-adjustment';
    const isAdjustmentModalOpen = isInternalAdjustmentModalOpen || isCustomerAdjustmentModalOpen;
    const adjustmentType = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'customer-adjustment' ? 'customer' : 'internal';
    const isBaseCostDialogOpen = (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'add-base-cost' ||
        (activeAction === null || activeAction === void 0 ? void 0 : activeAction.type) === 'edit-base-cost';
    return {
        // Active action state
        activeAction,
        shipment,
        isLoadingShipment,
        // Action triggers
        editCharge,
        addInternalAdjustment,
        addCustomerAdjustment,
        addBaseCost,
        clearAction,
        // Save handlers
        saveAdjustment,
        saveBaseCost,
        // Base cost dialog specific
        dialogEquipmentId,
        setDialogEquipmentId,
        dialogBaseCost,
        setDialogBaseCost,
        dialogFsc,
        setDialogFsc,
        dialogBaseCostInvoiced,
        dialogFscInvoiced,
        dialogOriginalBaseCostAmount,
        dialogOriginalFscAmount,
        dialogOriginalEquipmentId,
        isSavingBaseCost: adjustmentMutation.isLoading,
        // Utilities
        canAddAdjustment,
        orderNeedsBaseCost,
        // Modal states (derived)
        isEditModalOpen,
        isAdjustmentModalOpen,
        adjustmentType,
        isBaseCostDialogOpen,
        // For pending refetch (if needed externally)
        setPendingRefetch,
        // Currency pass-through
        currency,
    };
}
